/**
 * The glyphs `/raporty` draws, transcribed from KANON `Raporty` (`01-ankiety.html`) at its own
 * 24-unit scale and 2px round-capped stroke.
 *
 * Only the ones no shared module already carries. The eye, the lock, the chevron and the filter
 * funnel come from `shared/ui/AuthIcons` and `shared/ui/FilterIcon`, and a second drawing of any of
 * them here would be the near-duplicate the styling rules exist to stop.
 *
 * Every one is `aria-hidden`: each is either decoration beside a word, or the whole label of a
 * control that carries its name on the button itself.
 */

interface IconProps {
  readonly className?: string;
}

function Glyph({ className, children }: IconProps & { readonly children: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** The header's `⋯`. Three dots rather than an ellipsis character, so it keeps its size in any font. */
export function MoreIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

/**
 * The chevron every section head of the editor ends with, drawn ROTATED by its stylesheet.
 *
 * The export writes `transform:rotate(180deg)` on it, which points it up — none of these sections
 * collapses, so it is a mark rather than a control and it is `aria-hidden` like every other glyph
 * here. `shared/ui/AuthIcons` carries a chevron pointing RIGHT and none pointing down.
 */
export function ChevronDownIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="m6 9 6 6 6-6" />
    </Glyph>
  );
}

/** The way into the editor. */
export function PencilIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17z" />
      <path d="m13.5 6.5 3 3" />
    </Glyph>
  );
}

/** The mandatory form's mark. The one accent on the survey column. */
export function StarIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" />
    </Glyph>
  );
}

/** The cadence chip's clock. */
export function ClockIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Glyph>
  );
}

/* ── The section tiles ───────────────────────────────────────────────────────────────────────
 * One per `StandardQuestionCatalog` section. `wellbeing` takes the spark the canon gives the
 * trainer's own questions: the canon draws no wellbeing section, because the intake it draws has
 * none, and a check-in's is the one place it appears. */

export function PersonIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </Glyph>
  );
}

export function HeartIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M12 21s-7-4.6-9-9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c-2 4.4-9 9-9 9z" />
    </Glyph>
  );
}

export function DumbbellIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M3 10v4M6 8v8M18 8v8M21 10v4M6 12h12" />
    </Glyph>
  );
}

export function SparkIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" />
    </Glyph>
  );
}

/**
 * A recording. KANON `03-do-przejrzenia.html`'s own clip glyph, drawn on the tile that NAMES a
 * client's video in the answer pane — this screen mints no media URLs and plays nothing.
 */
export function ClipIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3z" />
    </Glyph>
  );
}

/* ── The editor's own glyphs, from KANON `04-edytor` and `05-edytor-dodaj-pytanie` ─────────────
 * The eight answer-type marks below are the canon's own drawings in its own 4 × 2 grid, and they
 * are the SAME marks the type pill on a question row wears — one drawing per answer type, so the
 * chip a trainer picks and the pill that then appears cannot read as two different things. */

/** Back to the list. */
export function BackIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </Glyph>
  );
}

/** „Usuń" — the survey's, and the shelf question's. */
export function TrashIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </Glyph>
  );
}

/**
 * The bookmark that saves a question to the shelf.
 *
 * `filled` is the state, not a variant: an outline says „save this" and a filled one says „already
 * saved", which is the whole of what the control reports.
 */
export function BookmarkIcon({ className, filled }: IconProps & { readonly filled?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled === true ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M6 3h12v18l-6-4-6 4z" />
    </svg>
  );
}

export function ShortTextIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 7h16M4 12h10M4 17h7" />
    </Glyph>
  );
}

export function LongTextIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 6h16M4 10h16M4 14h16M4 18h9" />
    </Glyph>
  );
}

export function SingleChoiceIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

export function MultiChoiceIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="m8 12 3 3 5-6" />
    </Glyph>
  );
}

export function NumberIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16" />
    </Glyph>
  );
}

export function ScaleIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M3 12h18M6 9v6M12 8v8M18 9v6" />
    </Glyph>
  );
}

/** The progress-photo question's mark — a standard check-in question, never an authorable type. */
export function PhotoIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </Glyph>
  );
}

/** The goal builder's mark — the answer type a survey can ask for. */
export function TargetIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    </Glyph>
  );
}

/* The floppy that stood here is GONE, and the app has one — `shared/ui/SaveGlyph`, the plan
 * builder's own drawing, promoted on 17 September 2026 when the editor's save became the builder's
 * square. This file's version was a second drawing of the same act at a different scale and stroke;
 * a trainer who saves a plan and a trainer who saves a form are the same trainer. */

/* ── One mark per PREDEFINED question (owner, 17 September 2026) ────────────────────────────────
 * `model/questionGlyphs` decides which code wears which; these are the drawings, in this file's own
 * style — a 24-unit box, round caps, `currentColor`. Eleven of the marks that mapping can answer
 * with are NOT here, because they are already drawn above as answer-type marks and the fallback
 * reuses them rather than adding a twelfth near-duplicate.
 *
 * The ink is the ROW's (`--text-3`, `--text-2` under the pointer), stated in `SurveyEditor.module.css`
 * and `SurveyDetailCard.module.css`. No colour on any of them: the accent on this screen is spent
 * on the weekday chip, the enabled switches and the mapping arrow.
 *
 * They go through `Glyph` at its stroke of 2, not at a lighter one: the answer-type mark on the SAME
 * ROW is drawn by `Glyph` too, and two weights a centimetre apart read as a mistake rather than as a
 * hierarchy. Two in a 24-unit box at 16px is 1.33 rendered, which is the weight the canon's rows
 * already carry. */

/** A birth date. A cake rather than a calendar, so „Dostępność" can have the calendar. */
export function CakeIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M4 20h16v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2z" />
      <path d="M8 12V9M12 12V8M16 12V9" />
      <path d="M4 16c2.7 1.6 5.3-1.6 8 0s5.3-1.6 8 0" />
    </Glyph>
  );
}

/** A height in centimetres — a measure between two marks. */
export function HeightIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M5 4h14M5 20h14M12 7v10" />
      <path d="m9 10 3-3 3 3M9 14l3 3 3-3" />
    </Glyph>
  );
}

/** A body weight — a bathroom scale seen from above, with its dial. */
export function WeightIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      {/* The dial takes nearly the whole width, and the needle points out of it. Drawn smaller and
        * higher up it read as an empty box at 16px — checked against a render, 17 September 2026. */}
      <path d="M7.5 15a4.5 4.5 0 0 1 9 0" />
      <path d="m12 15 2.5-3.5" />
    </Glyph>
  );
}

/** Past injuries — a dressing over the place. */
export function BandageIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      {/* Seen STRAIGHT ON, with its pad, and that is not a style choice: „Kontuzje" and „Leki" are
        * two rows of the same section, and a bandage drawn as a rotated capsule was
        * indistinguishable from the pill beside it at 16px — checked against a render, 17 September
        * 2026. The horizontal plaster and the diagonal capsule cannot be confused. */}
      <rect x="2" y="8" width="20" height="8" rx="4" />
      <rect x="9" y="8" width="6" height="8" />
    </Glyph>
  );
}

/** Regular medications. */
export function PillIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <rect x="2.5" y="8" width="19" height="8" rx="4" transform="rotate(-45 12 12)" />
      <path d="m9 9 6 6" />
    </Glyph>
  );
}

/** A daily activity level, and the mark a „Skala" question falls back to. */
export function ActivityIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M3 12h4l2.5-6 4 13 2.5-7h5" />
    </Glyph>
  );
}

/** Training experience. */
export function MedalIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <circle cx="12" cy="15" r="5" />
      <path d="M8.5 10.5 6 3h12l-2.5 7.5" />
    </Glyph>
  );
}

/** Dietary preferences. */
export function UtensilsIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M6 3v7a2.5 2.5 0 0 0 5 0V3M8.5 12v9" />
      <path d="M16.5 3v18M14 3c0 3.5 5 3.5 5 0" />
    </Glyph>
  );
}

/** Sleep quality. */
export function MoonIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
    </Glyph>
  );
}

/** Energy level. */
export function BoltIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <path d="M13.5 3 6 13.5h5L10.5 21 18 10.5h-5z" />
    </Glyph>
  );
}

/** The full-body measurement — girths, so a tape. */
export function RulerIcon({ className }: IconProps) {
  return (
    <Glyph className={className}>
      <rect x="2" y="8" width="20" height="8" rx="2" />
      <path d="M7 8v3M12 8v4M17 8v3" />
    </Glyph>
  );
}
